
import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar as CalendarIcon, Plus, Filter, Search, ChevronLeft, ChevronRight,
  CheckSquare, FileText, AlertTriangle, X, XCircle, Camera, CheckCircle, Clock,
  DollarSign, ArrowRight, Loader2, Download, ArrowUpDown, ArrowUp, ArrowDown, Pencil
} from 'lucide-react';
import { Button, Input, Badge, Card, Modal, FieldHint, InfoTooltip, useToast } from './ui';
import { SignaturePad } from './SignaturePad';
import { ProductPicker } from './ProductPicker';
import { DocStatus, ContractType } from '../types';
import type { Contract, Machine, Client, ConditionReport } from '../types';
import { CHECKLIST_TEMPLATE, getChecklistForCategory } from '../constants';
import {
  rentalService,
  RentalContract,
  RentalContractUpdate,
  clientsService,
  stockService,
  filesService,
  loaService,
} from '../src/services';
import { usePaymentMethods } from '../src/hooks/usePaymentMethods';
import { PaymentMethodSelect, PaymentDetails, EMPTY_PAYMENT_DETAILS, isPaymentValid } from './PaymentFields';

// Transform API client to local format
const transformClient = (c: any): Client => ({
  id: c.id,
  code: c.code || '',
  name: c.nom || c.contact_principal || '',
  company: c.raison_sociale || '',
  type: c.type_client || 'PROFESSIONNEL',
  provenance: c.provenance_libelle || '',
  email: c.email || '',
  phone: c.telephone || '',
  address: c.adresse || '',
  city: c.ville || '',
  isVatExempt: c.is_exonere || false,
  isActive: c.is_active !== false,
  solvencyScore: c.score_solvabilite || 50,
});

// Transform API contract to local format
const transformContract = (c: RentalContract): Contract & { uuid: string } => ({
  id: c.code || c.id,
  uuid: c.id,
  type: ContractType.RENTAL,
  clientId: c.client_id,
  clientName: c.client_nom,
  machineId: c.machine_id,
  machineName: c.machine_nom,
  startDate: c.date_debut,
  endDate: c.date_fin_prevue || c.date_fin_reelle || '',
  status: mapContractStatus(c.status),
  totalAmount: c.montant_total_prevu || 0,
  depositAmount: c.montant_caution || 0,
  depositStatus: c.caution_payee ? 'PAID' : 'PENDING',
  cautionRestituee: c.caution_restituee || false,
  entryReportId: c.etat_lieux_entree_id,
  exitReportId: c.etat_lieux_sortie_id,
});

// Map contract status to DocStatus
const mapContractStatus = (status: string): DocStatus => {
  const statusMap: Record<string, DocStatus> = {
    'DRAFT': DocStatus.DRAFT,
    'PENDING_APPROVAL': DocStatus.PENDING_APPROVAL,
    'VALIDATED': DocStatus.VALIDATED,
    'CAUTION_PAYEE': DocStatus.CAUTION_PAYEE,
    'ACTIVE': DocStatus.ACTIVE,
    'CLOSED': DocStatus.CLOSED,
    'CANCELLED': DocStatus.CANCELLED,
  };
  return statusMap[status] || DocStatus.DRAFT;
};

// Transform API machine/product to local format
const transformMachine = (p: any): Machine => ({
  id: p.id,
  name: p.designation,
  serial: p.numero_serie || '',
  category: p.categorie_nom || '',
  status: p.status || 'AVAILABLE',
  location: 'Cotonou',
  dailyRate: p.tarif_location_journalier || 0,
  purchasePrice: p.prix_vente_public || 0,
  imageUrl: p.image_url || '',
  stockTotal: p.stock_total ?? 0,
});

const formatCurrency = (val: number) => new Intl.NumberFormat('fr-BJ', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(val);

// --- Sub-components ---

const ContractStatusBadge = ({ status }: { status: DocStatus }) => {
  const styles = {
    [DocStatus.DRAFT]: 'yellow',
    [DocStatus.PENDING_APPROVAL]: 'orange',
    [DocStatus.VALIDATED]: 'blue',
    [DocStatus.CAUTION_PAYEE]: 'purple',
    [DocStatus.ACTIVE]: 'green',
    [DocStatus.CLOSED]: 'gray',
    [DocStatus.CANCELLED]: 'red',
  };
  const labels: Record<string, string> = {
    [DocStatus.DRAFT]: 'Brouillon',
    [DocStatus.PENDING_APPROVAL]: 'En attente',
    [DocStatus.VALIDATED]: 'Validé',
    [DocStatus.CAUTION_PAYEE]: 'Caution payée',
    [DocStatus.ACTIVE]: 'Actif',
    [DocStatus.CLOSED]: 'Clôturé',
    [DocStatus.CANCELLED]: 'Annulé',
  };
  return <Badge color={styles[status] as any}>{labels[status] || status}</Badge>;
};

// --- Calendar Component ---

interface RentalCalendarProps {
  machines: Machine[];
  contracts: Contract[];
  isLoading: boolean;
}

const MONTH_NAMES_SHORT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

// A calendar row: either a serialized machine, a specific contract for a non-serialized product, or available stock
interface CalendarRow {
  id: string; // unique row key
  machineId: string;
  label: string;
  sublabel: string;
  contract?: Contract; // pinned contract for non-serialized product rows
  isAvailableRow?: boolean; // row representing remaining available stock
  availableCount?: number;
}

const RentalCalendar = ({ machines, contracts, isLoading }: RentalCalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarMode, setCalendarMode] = useState<'month' | 'year'>('month');
  const [popover, setPopover] = useState<{ x: number; y: number; contract: Contract; machineName: string } | null>(null);

  const goToToday = () => setCurrentMonth(new Date());

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const prevYear = () => setCurrentMonth(new Date(currentMonth.getFullYear() - 1, currentMonth.getMonth(), 1));
  const nextYear = () => setCurrentMonth(new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth(), 1));

  // Build expanded rows: non-serialized products with contracts get one row per contract
  const calendarRows: CalendarRow[] = React.useMemo(() => {
    const rows: CalendarRow[] = [];
    for (const m of machines) {
      const isSerialized = !!m.serial;
      // Get active contracts for this machine (not cancelled/closed)
      const machineContracts = contracts.filter(
        c => c.machineId === m.id && c.status !== DocStatus.CANCELLED && c.status !== DocStatus.CLOSED
      );

      if (isSerialized || machineContracts.length <= 1) {
        // Serialized or 0-1 contracts: single row as before
        rows.push({
          id: m.id,
          machineId: m.id,
          label: m.name,
          sublabel: !isSerialized && (m.stockTotal ?? 0) > 0 ? `Qté: ${m.stockTotal}` : m.serial || m.location,
        });
      } else {
        // Non-serialized with multiple contracts: one row per contract
        machineContracts.forEach((c, idx) => {
          rows.push({
            id: `${m.id}__contract__${(c as any).uuid || c.id}`,
            machineId: m.id,
            label: m.name,
            sublabel: `${c.clientName || c.id} (${c.startDate} → ${c.endDate})`,
            contract: c,
          });
        });
        // Row for remaining available units
        const stockTotal = m.stockTotal ?? 0;
        const availableCount = Math.max(0, stockTotal - machineContracts.length);
        if (availableCount > 0) {
          rows.push({
            id: `${m.id}__available`,
            machineId: m.id,
            label: m.name,
            sublabel: `Disponible (${availableCount})`,
            isAvailableRow: true,
            availableCount,
          });
        }
      }
    }
    return rows;
  }, [machines, contracts]);

  const getContractForDay = (machineId: string, day: number, month?: number, year?: number, pinnedContract?: Contract) => {
    const m = month ?? currentMonth.getMonth();
    const y = year ?? currentMonth.getFullYear();
    const cellDate = new Date(y, m, day);
    cellDate.setHours(0, 0, 0, 0);

    // If a pinned contract is provided (expanded non-serialized row), only check that contract
    if (pinnedContract) {
      if (pinnedContract.status === DocStatus.CANCELLED || pinnedContract.status === DocStatus.CLOSED) return null;
      const start = new Date(pinnedContract.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(pinnedContract.endDate);
      end.setHours(0, 0, 0, 0);
      return cellDate >= start && cellDate <= end ? pinnedContract : null;
    }

    return contracts.find(c => {
      if (c.machineId !== machineId || c.status === DocStatus.CANCELLED || c.status === DocStatus.CLOSED) return false;
      const start = new Date(c.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(c.endDate);
      end.setHours(0, 0, 0, 0);
      return cellDate >= start && cellDate <= end;
    }) || null;
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isContractOverdue = (contract: Contract) => {
    // Active contract (machine on site) + end date passed + no exit report → overdue return
    if (contract.status === DocStatus.ACTIVE && !contract.exitReportId) {
      const end = new Date(contract.endDate);
      end.setHours(0, 0, 0, 0);
      if (today > end) return true;
    }
    // Validated/reserved contract + start date passed + no entry report → overdue departure
    if (contract.status === DocStatus.VALIDATED && !contract.entryReportId) {
      const start = new Date(contract.startDate);
      start.setHours(0, 0, 0, 0);
      if (today > start) return true;
    }
    return false;
  };

  const getDayStatus = (machineId: string, day: number, month?: number, year?: number) => {
    const contract = getContractForDay(machineId, day, month, year);
    if (contract) {
      if (isContractOverdue(contract)) return 'bg-red-500';
      if (contract.type === ContractType.LOA) return 'bg-purple-500';
      if (contract.status === DocStatus.ACTIVE) return 'bg-green-500';
      return 'bg-blue-400';
    }
    return 'bg-slate-100';
  };

  // For annual view: compute monthly occupancy ratio per machine (or pinned contract)
  const getMonthOccupancy = (machineId: string, month: number, year: number, pinnedContract?: Contract) => {
    const daysCount = new Date(year, month + 1, 0).getDate();
    let occupiedDays = 0;
    let activeDays = 0;
    let loaDays = 0;
    let overdueDays = 0;
    for (let d = 1; d <= daysCount; d++) {
      const contract = getContractForDay(machineId, d, month, year, pinnedContract);
      if (contract) {
        occupiedDays++;
        if (isContractOverdue(contract)) overdueDays++;
        else if (contract.type === ContractType.LOA) loaDays++;
        else if (contract.status === DocStatus.ACTIVE) activeDays++;
      }
    }
    return { occupiedDays, activeDays, loaDays, overdueDays, daysCount };
  };

  const getMonthCellColor = (machineId: string, month: number, year: number, pinnedContract?: Contract) => {
    const { occupiedDays, activeDays, loaDays, overdueDays, daysCount } = getMonthOccupancy(machineId, month, year, pinnedContract);
    if (occupiedDays === 0) return 'bg-slate-100';
    const ratio = daysCount > 0 ? occupiedDays / daysCount : 0;
    if (overdueDays > 0) {
      // Red tones for overdue (return or departure not registered)
      if (overdueDays >= occupiedDays / 2) return 'bg-red-500';
      return 'bg-red-400';
    }
    if (loaDays > 0) {
      // Purple tones for LOA
      if (ratio >= 0.5) return 'bg-purple-500';
      return 'bg-purple-400';
    }
    if (activeDays > 0) {
      // Green tones for active
      if (ratio >= 0.8) return 'bg-green-600';
      if (ratio >= 0.5) return 'bg-green-500';
      if (ratio >= 0.2) return 'bg-green-400';
      return 'bg-green-300';
    }
    // Blue tones for reserved only
    if (ratio >= 0.5) return 'bg-blue-500';
    if (ratio >= 0.2) return 'bg-blue-400';
    return 'bg-blue-300';
  };

  const getMonthCellTooltip = (machineId: string, month: number, year: number, pinnedContract?: Contract) => {
    const { occupiedDays, daysCount } = getMonthOccupancy(machineId, month, year, pinnedContract);
    const pct = daysCount > 0 ? Math.round((occupiedDays / daysCount) * 100) : 0;
    return `${MONTH_NAMES_SHORT[month]} ${year}: ${occupiedDays}/${daysCount} jours occupés (${pct}%)`;
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col h-[500px]">
      {/* Calendar Header */}
      <div className="p-4 border-b border-slate-200 flex flex-wrap gap-2 justify-between items-center bg-slate-50">
        <h3 className="font-bold text-base text-slate-800 flex items-center gap-2 min-w-0">
          <CalendarIcon className="w-5 h-5 text-primary-600 flex-shrink-0" />
          <span className="truncate">
          {calendarMode === 'month'
            ? `Planning - ${currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`
            : `Planning Annuel - ${currentMonth.getFullYear()}`
          }
          </span>
        </h3>
        <div className="flex gap-1 items-center flex-shrink-0">
          {/* Mode toggle */}
          <div className="flex bg-white border border-slate-300 rounded overflow-hidden mr-1">
            <button
              onClick={() => setCalendarMode('month')}
              className={`text-xs px-2 py-1 font-medium ${calendarMode === 'month' ? 'bg-primary-50 text-primary-700' : 'text-slate-500 hover:bg-slate-50'}`}
            >Mois</button>
            <button
              onClick={() => setCalendarMode('year')}
              className={`text-xs px-2 py-1 font-medium ${calendarMode === 'year' ? 'bg-primary-50 text-primary-700' : 'text-slate-500 hover:bg-slate-50'}`}
            >Année</button>
          </div>
          <button onClick={calendarMode === 'month' ? prevMonth : prevYear} className="p-1 hover:bg-slate-200 rounded"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={goToToday} className="text-xs px-2 py-1 bg-white border border-slate-300 rounded hover:bg-slate-50">Auj.</button>
          <button onClick={calendarMode === 'month' ? nextMonth : nextYear} className="p-1 hover:bg-slate-200 rounded"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          </div>
        ) : machines.length === 0 ? (
          <div className="flex justify-center items-center h-full text-slate-500">
            Aucune machine disponible
          </div>
        ) : calendarMode === 'month' ? (
        <div className="inline-flex flex-col min-w-full">
           {/* Header Row */}
           <div className="flex border-b border-slate-200 sticky top-0 bg-white z-20">
             <div className="w-36 min-w-[9rem] flex-shrink-0 p-2 font-bold text-xs text-slate-500 uppercase sticky left-0 bg-white border-r border-slate-200 z-30 shadow-sm">Machine</div>
             {days.map(d => (
               <div key={d} className="w-8 flex-shrink-0 text-center text-[10px] text-slate-500 border-r border-slate-100 py-2">
                 {d}
               </div>
             ))}
           </div>

           {/* Machine Rows (expanded for non-serialized products with multiple contracts) */}
           {calendarRows.map(row => (
             <div key={row.id} className={`flex border-b border-slate-100 hover:bg-slate-50 ${row.isAvailableRow ? 'opacity-60' : ''}`}>
               <div className="w-36 min-w-[9rem] flex-shrink-0 p-2 text-xs font-medium text-slate-800 sticky left-0 bg-white border-r border-slate-200 z-10 flex flex-col justify-center group relative">
                 <span className="truncate">{row.label}</span>
                 <span className="text-[10px] text-slate-400 truncate">{row.sublabel}</span>
                 <div className="absolute left-full top-1/2 -translate-y-1/2 ml-1 px-2 py-1 bg-slate-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-50 hidden group-hover:block pointer-events-none">
                   {row.label} — {row.sublabel}
                 </div>
               </div>
               {days.map(d => {
                 if (row.isAvailableRow) {
                   return (
                     <div key={d} className="w-8 flex-shrink-0 border-r border-slate-100 h-10 relative p-0.5">
                       <div className="w-full h-full rounded-sm bg-slate-100" />
                     </div>
                   );
                 }
                 const contract = getContractForDay(row.machineId, d, undefined, undefined, row.contract);
                 const statusClass = contract
                   ? (isContractOverdue(contract) ? 'bg-red-500' : contract.type === ContractType.LOA ? 'bg-purple-500' : contract.status === DocStatus.ACTIVE ? 'bg-green-500' : 'bg-blue-400')
                   : 'bg-slate-100';
                 return (
                   <div key={d} className="w-8 flex-shrink-0 border-r border-slate-100 h-10 relative p-0.5">
                     <div
                       className={`w-full h-full rounded-sm ${statusClass} ${contract ? 'cursor-pointer hover:opacity-80' : ''}`}
                       onClick={(e) => {
                         if (contract) {
                           const rect = e.currentTarget.getBoundingClientRect();
                           setPopover({ x: rect.left, y: rect.bottom + 4, contract, machineName: row.label });
                         }
                       }}
                     />
                   </div>
                 );
               })}
             </div>
           ))}
        </div>
        ) : (
        /* Annual View */
        <div className="min-w-[600px]">
           {/* Header Row - Months */}
           <div className="flex border-b border-slate-200">
             <div className="w-48 min-w-[12rem] flex-shrink-0 p-3 font-bold text-xs text-slate-500 uppercase sticky left-0 bg-white border-r border-slate-200 z-10 shadow-sm">Machine</div>
             {MONTH_NAMES_SHORT.map((name, i) => (
               <div key={i} className={`flex-1 min-w-[60px] text-center text-xs font-medium border-r border-slate-100 py-2 ${i === new Date().getMonth() && currentMonth.getFullYear() === new Date().getFullYear() ? 'text-primary-700 bg-primary-50' : 'text-slate-500'}`}>
                 {name}
               </div>
             ))}
           </div>

           {/* Machine Rows (expanded for non-serialized products with multiple contracts) */}
           {calendarRows.map(row => (
             <div key={row.id} className={`flex border-b border-slate-100 hover:bg-slate-50 ${row.isAvailableRow ? 'opacity-60' : ''}`}>
               <div className="w-48 min-w-[12rem] flex-shrink-0 p-3 text-sm font-medium text-slate-800 sticky left-0 bg-white border-r border-slate-200 z-10 flex flex-col justify-center">
                 <span>{row.label}</span>
                 <span className="text-xs text-slate-400 truncate">{row.sublabel}</span>
               </div>
               {MONTH_NAMES_SHORT.map((_, monthIdx) => {
                 if (row.isAvailableRow) {
                   return (
                     <div key={monthIdx} className="flex-1 min-w-[60px] border-r border-slate-100 h-12 relative p-1">
                       <div className="w-full h-full rounded-sm bg-slate-100" />
                     </div>
                   );
                 }
                 const { occupiedDays, daysCount } = getMonthOccupancy(row.machineId, monthIdx, currentMonth.getFullYear(), row.contract);
                 const pct = daysCount > 0 ? Math.round((occupiedDays / daysCount) * 100) : 0;
                 return (
                   <div
                     key={monthIdx}
                     className="flex-1 min-w-[60px] border-r border-slate-100 h-12 relative p-1 cursor-pointer"
                     title={getMonthCellTooltip(row.machineId, monthIdx, currentMonth.getFullYear(), row.contract)}
                     onClick={() => {
                       setCurrentMonth(new Date(currentMonth.getFullYear(), monthIdx, 1));
                       setCalendarMode('month');
                     }}
                   >
                     <div className={`w-full h-full rounded-sm flex items-center justify-center ${getMonthCellColor(row.machineId, monthIdx, currentMonth.getFullYear(), row.contract)}`}>
                       {occupiedDays > 0 && (
                         <span className="text-[10px] font-bold text-white drop-shadow-sm">{pct}%</span>
                       )}
                     </div>
                   </div>
                 );
               })}
             </div>
           ))}
        </div>
        )}
      </div>

      {/* Contract popover */}
      {popover && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => setPopover(null)}
        >
          <div
            className="absolute bg-white border border-slate-200 rounded-lg shadow-lg p-3 w-64 z-50"
            style={{ left: Math.min(popover.x, window.innerWidth - 270), top: Math.min(popover.y, window.innerHeight - 200) }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-2">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isContractOverdue(popover.contract) ? 'bg-red-100 text-red-700' : popover.contract.type === ContractType.LOA ? 'bg-purple-100 text-purple-700' : popover.contract.status === DocStatus.ACTIVE ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                {isContractOverdue(popover.contract) ? (popover.contract.status === DocStatus.ACTIVE ? 'Retour en retard' : 'Départ en retard') : popover.contract.type === ContractType.LOA ? 'LOA' : popover.contract.status === DocStatus.ACTIVE ? 'En cours' : 'Réservé'}
              </span>
              <button onClick={() => setPopover(null)} className="text-slate-400 hover:text-slate-600 text-sm leading-none">&times;</button>
            </div>
            <div className="space-y-1 text-sm">
              <div><span className="text-slate-500">Contrat:</span> <span className="font-medium text-slate-800">{popover.contract.id}</span></div>
              <div><span className="text-slate-500">Client:</span> <span className="font-medium text-slate-800">{popover.contract.clientName}</span></div>
              <div><span className="text-slate-500">Machine:</span> <span className="font-medium text-slate-800">{popover.machineName}</span></div>
              <div><span className="text-slate-500">Période:</span> <span className="font-medium text-slate-800">{popover.contract.startDate} → {popover.contract.endDate}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="p-2 border-t border-slate-200 flex gap-4 text-xs">
         <div className="flex items-center gap-1"><div className="w-3 h-3 bg-green-500 rounded-sm"></div> En cours</div>
         <div className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-400 rounded-sm"></div> Réservé</div>
         <div className="flex items-center gap-1"><div className="w-3 h-3 bg-red-500 rounded-sm"></div> En retard</div>
         <div className="flex items-center gap-1"><div className="w-3 h-3 bg-purple-500 rounded-sm"></div> LOA</div>
         <div className="flex items-center gap-1"><div className="w-3 h-3 bg-slate-100 rounded-sm border border-slate-300"></div> Disponible</div>
         {calendarMode === 'year' && (
           <div className="flex items-center gap-1 ml-2 text-slate-400">| Cliquer sur un mois pour le détail</div>
         )}
      </div>
    </div>
  );
};

// --- Condition Report (Etat des Lieux) - MOBILE FIRST OPTIMIZED ---

interface ConditionReportViewProps {
  type: 'ENTRY' | 'EXIT';
  contract: Contract;
  machineCategory?: string;
  userRole?: string;
  userName?: string;
  onClose: () => void;
  onSave: () => void;
}

interface PhotoFile {
  file: File;
  previewUrl: string;
}

const ConditionReportView = ({ type, contract, machineCategory, userRole, userName, onClose, onSave }: ConditionReportViewProps) => {
  const [step, setStep] = useState(1);
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [checklist, setChecklist] = useState<Record<string, boolean>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [agentSignature, setAgentSignature] = useState('');
  const [clientSignature, setClientSignature] = useState('');
  const [nomAgent, setNomAgent] = useState(userName || '');
  const [nomClient, setNomClient] = useState((contract as any).clientName || '');
  const [releveCompteur, setReleveCompteur] = useState<string>('');
  const [niveauCarburant, setNiveauCarburant] = useState<string>('');
  const [observations, setObservations] = useState('');
  const [dommagesConstates, setDommagesConstates] = useState('');
  const { toast } = useToast();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const isEntry = type === 'ENTRY';
  const labelType = isEntry ? 'ENTRÉE' : 'SORTIE';
  const totalSteps = 3;
  const checklistItems = getChecklistForCategory(machineCategory);
  const isMagasinierUser = userRole === 'MAGASINIER';
  const agentSignatureLabel = isMagasinierUser ? 'Inspecteur (Magasinier)' : 'Agent Agri-Manager';

  const handleNext = () => setStep(s => s + 1);

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newPhotos: PhotoFile[] = [];
    Array.from(files).forEach(file => {
      if (file.type.startsWith('image/')) {
        newPhotos.push({
          file,
          previewUrl: URL.createObjectURL(file),
        });
      }
    });

    setPhotos(prev => [...prev, ...newPhotos]);
    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = (index: number) => {
    setPhotos(prev => {
      const newPhotos = [...prev];
      // Revoke the object URL to free memory
      URL.revokeObjectURL(newPhotos[index].previewUrl);
      newPhotos.splice(index, 1);
      return newPhotos;
    });
  };

  // Keep a ref to the latest photos for cleanup on unmount
  const photosRef = React.useRef(photos);
  React.useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  // Cleanup preview URLs on unmount
  React.useEffect(() => {
    return () => {
      photosRef.current.forEach(p => URL.revokeObjectURL(p.previewUrl));
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-slate-100 flex flex-col h-full w-full">
      {/* Mobile-First Header (Fixed Top) */}
      <div className={`flex-none px-4 py-3 ${isEntry ? 'bg-primary-600' : 'bg-orange-600'} text-white shadow-md flex justify-between items-center z-10`}>
         <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1"><ChevronLeft className="w-6 h-6" /></button>
            <div>
               <h2 className="text-lg font-bold leading-tight">État des Lieux : {labelType}</h2>
               <p className="text-xs opacity-90">{contract.machineName}</p>
            </div>
         </div>
         <div className="text-xs font-bold bg-white/20 px-2 py-1 rounded">
           {step}/{totalSteps}
         </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto p-4 pb-24">
         {step === 1 && (
            <div className="space-y-4 animate-in slide-in-from-right duration-200">
               <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                  <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
                     <CheckSquare className="w-5 h-5 text-primary-600" /> Inspection Visuelle
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">Cochez si l'état est conforme (BON).</p>
                  
                  {machineCategory && (
                    <p className="text-xs text-primary-600 font-medium mb-3 bg-primary-50 px-2 py-1 rounded">
                      Checklist adaptée : {machineCategory}
                    </p>
                  )}
                  <div className="space-y-3">
                    {checklistItems.map(item => (
                      <label key={item.key} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg active:bg-slate-50 touch-manipulation">
                        <span className="text-sm font-medium text-slate-800">{item.label}</span>
                        <input
                          type="checkbox"
                          className="h-6 w-6 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          checked={checklist[item.key] || false}
                          onChange={e => setChecklist({...checklist, [item.key]: e.target.checked})}
                        />
                      </label>
                    ))}
                  </div>
               </div>

               <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                  <h3 className="font-bold text-slate-800 mb-3">Relevés & Observations</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Relevé compteur (km/h)</label>
                      <input type="number" className="w-full border-slate-300 rounded-lg text-sm py-2 px-3" placeholder="Ex: 1250" value={releveCompteur} onChange={e => setReleveCompteur(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Niveau carburant</label>
                      <select className="w-full border-slate-300 rounded-lg text-sm py-2 px-3" value={niveauCarburant} onChange={e => setNiveauCarburant(e.target.value)}>
                        <option value="">-- Sélectionner --</option>
                        <option value="PLEIN">Plein</option>
                        <option value="3/4">3/4</option>
                        <option value="1/2">1/2</option>
                        <option value="1/4">1/4</option>
                        <option value="VIDE">Vide</option>
                        <option value="N/A">N/A (électrique/manuel)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Observations</label>
                      <textarea className="w-full border-slate-300 rounded-lg text-sm py-2 px-3" rows={2} placeholder="Observations générales..." value={observations} onChange={e => setObservations(e.target.value)} />
                    </div>
                    {!isEntry && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Dommages constatés</label>
                        <textarea className="w-full border-slate-300 rounded-lg text-sm py-2 px-3" rows={2} placeholder="Décrire les dommages éventuels..." value={dommagesConstates} onChange={e => setDommagesConstates(e.target.value)} />
                      </div>
                    )}
                  </div>
               </div>
            </div>
         )}

         {step === 2 && (
            <div className="space-y-4 animate-in slide-in-from-right duration-200">
               <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 text-center">
                  <h3 className="font-bold text-slate-800 mb-2">Preuves Photos</h3>

                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    capture="environment"
                    className="hidden"
                    onChange={handlePhotoSelect}
                  />

                  <div
                    className="my-6 border-2 border-dashed border-slate-300 rounded-xl p-8 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                     <Camera className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                     <p className="font-medium text-slate-600">Appuyer pour ajouter</p>
                     <p className="text-xs text-slate-400 mt-1">Min. 4 photos requises</p>
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    {photos.map((photo, i) => (
                      <div key={i} className="aspect-square bg-slate-200 rounded-lg overflow-hidden relative border border-slate-300 group">
                          <img src={photo.previewUrl} className="object-cover w-full h-full" alt={`Photo ${i + 1}`} />
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleRemovePhoto(i); }}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full border-2 border-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                          <div className="absolute bottom-1 left-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                            <CheckCircle className="w-3 h-3 text-white" />
                          </div>
                      </div>
                    ))}
                  </div>
               </div>
               {photos.length < 4 && (
                 <div className="bg-red-50 text-red-600 p-3 rounded-lg text-xs flex gap-2 items-center">
                   <AlertTriangle className="w-4 h-4" /> Il manque {4 - photos.length} photos obligatoires.
                 </div>
               )}
            </div>
         )}

         {step === 3 && (
            <div className="space-y-4 animate-in slide-in-from-right duration-200">
               <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                  <h3 className="font-bold text-slate-800 mb-4">Signatures Requises</h3>
                  <div className="space-y-6">
                     <Input label={`Nom ${agentSignatureLabel}`} required value={nomAgent} onChange={e => setNomAgent(e.target.value)} placeholder="Nom complet de l'agent" />
                     <SignaturePad label={agentSignatureLabel} onSave={(sig: string) => setAgentSignature(sig)} />
                     <div className="border-t border-slate-100"></div>
                     <Input label="Nom du Client (Locataire)" required value={nomClient} onChange={e => setNomClient(e.target.value)} placeholder="Nom complet du client" />
                     <SignaturePad label="Client (Locataire)" onSave={(sig: string) => setClientSignature(sig)} />
                  </div>
               </div>
               <p className="text-xs text-slate-500 text-center px-4">
                 En signant ce document, les parties valident l'état du matériel à la date du {new Date().toLocaleDateString()}.
               </p>
            </div>
         )}
      </div>

      {/* Sticky Footer (Mobile First) */}
      <div className="flex-none p-4 bg-white border-t border-slate-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-20">
         <div className="flex gap-3">
            {step > 1 && (
               <Button variant="secondary" onClick={() => setStep(s => s - 1)} className="flex-1 py-3 text-base">
                 Retour
               </Button>
            )}
            {step < totalSteps ? (
               <Button onClick={handleNext} className="flex-1 py-3 text-base shadow-lg" disabled={step === 2 && photos.length < 4}>
                 Suivant
               </Button>
            ) : (
               <Button
                 disabled={isSaving || !agentSignature || !clientSignature || nomAgent.trim().length < 2 || nomClient.trim().length < 2}
                 onClick={async () => {
                   setIsSaving(true);
                   try {
                     const contractUuid = (contract as any).uuid || contract.id;

                     // 1. Upload all photos to MinIO first to get URLs
                     const photoEntries: { url: string; description?: string; ordre: number }[] = [];
                     for (let i = 0; i < photos.length; i++) {
                       try {
                         const uploaded = await filesService.upload(photos[i].file, {
                           category: 'condition_photo',
                         });
                         photoEntries.push({
                           url: uploaded.url,
                           ordre: i + 1,
                         });
                       } catch {
                         console.error('Failed to upload photo', i);
                       }
                     }

                     // 2. Create condition report with everything in one call
                     await rentalService.createConditionReport(contractUuid, {
                       type_etat: isEntry ? 'ENTREE' : 'SORTIE',
                       checklist: checklist,
                       signature_client: clientSignature,
                       signature_agent: agentSignature,
                       nom_client: nomClient.trim(),
                       nom_agent: nomAgent.trim(),
                       photos: photoEntries,
                       ...(releveCompteur ? { releve_compteur: Number(releveCompteur) } : {}),
                       ...(niveauCarburant ? { niveau_carburant: niveauCarburant } : {}),
                       ...(observations.trim() ? { observations: observations.trim() } : {}),
                       ...(!isEntry && dommagesConstates.trim() ? { dommages_constates: dommagesConstates.trim() } : {}),
                     });

                     toast('État des lieux validé avec succès', 'success');
                     onSave();
                   } catch (err: any) {
                     const detail = err?.response?.data?.detail || err.message || 'Impossible de valider';
                     toast('Erreur: ' + (typeof detail === 'string' ? detail : JSON.stringify(detail)), 'error');
                   } finally {
                     setIsSaving(false);
                   }
                 }}
                 className="flex-1 py-3 text-base bg-green-600 hover:bg-green-700 shadow-lg"
               >
                 {isSaving ? 'Validation en cours...' : 'Valider & Signer'}
               </Button>
            )}
         </div>
      </div>
    </div>
  );
}

// --- Create Rental Wizard ---

interface CreateRentalWizardProps {
  onClose: () => void;
  onSave: (data: Partial<Contract>) => void;
  machines: Machine[];
  clients: Client[];
  contracts: Contract[];
  isSaving?: boolean;
}

const CreateRentalWizard = ({ onClose, onSave, machines, clients, contracts, isSaving }: CreateRentalWizardProps) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Partial<Contract>>({
    type: ContractType.RENTAL,
    status: DocStatus.VALIDATED,
    depositStatus: 'PENDING'
  });
  const [error, setError] = useState('');

  const [selectedMachine, setSelectedMachine] = useState<Machine>();

  const findConflict = (startDate?: string, endDate?: string, machineId?: string) => {
    if (!startDate || !endDate || !machineId) return null;
    const machine = selectedMachine?.id === machineId ? selectedMachine : machines.find(m => m.id === machineId);
    const isSerialized = machine && !!machine.serial;
    const overlapping = contracts.filter(c => {
      if (c.machineId !== machineId || c.status === DocStatus.CANCELLED || c.status === DocStatus.CLOSED) return false;
      return startDate <= c.endDate && endDate >= c.startDate;
    });
    if (isSerialized) {
      return overlapping[0] || null;
    }
    // Non-serialized: conflict only if all stock units are taken
    const stockTotal = machine?.stockTotal ?? 0;
    if (overlapping.length >= stockTotal) {
      return overlapping[0] || null;
    }
    return null;
  };

  const dateConflict = findConflict(formData.startDate, formData.endDate, formData.machineId);

  const validateAvailability = () => {
    if (dateConflict) {
      setError(`Machine déjà louée du ${dateConflict.startDate} au ${dateConflict.endDate}`);
      return false;
    }
    setError('');
    return true;
  };

  const calculateTotal = () => {
    if (selectedMachine && formData.startDate && formData.endDate) {
       const days = Math.floor((new Date(formData.endDate).getTime() - new Date(formData.startDate).getTime()) / (1000 * 3600 * 24));
       const total = days * selectedMachine.dailyRate;
       return { days, total };
    }
    return { days: 0, total: 0 };
  };

  const { days, total } = calculateTotal();

  return (
    <Modal isOpen={true} onClose={onClose} title="Nouveau Contrat de Location">
      <div className="space-y-4">
        {/* Steps Indicator */}
        <div className="flex border-b border-slate-200 mb-4">
          {[1, 2, 3, 4].map(s => (
            <div key={s} className={`flex-1 py-2 text-center text-xs font-medium border-b-2 ${step === s ? 'border-primary-500 text-primary-700' : 'border-transparent text-slate-400'}`}>
              Etape {s}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
             <label className="block text-sm font-medium text-slate-700">Sélectionner la Machine <span className="text-red-500 ml-0.5">*</span></label>
             <ProductPicker value={formData.machineId || ''} type="MACHINE" machineOnly dailyRate
               onSelect={p => {
                 const machine = transformMachine(p);
                 setSelectedMachine(machine);
                 setFormData(data => ({ ...data, machineId: machine.id, machineName: machine.name }));
               }} />
             <Button className="w-full mt-2" onClick={() => setStep(2)} disabled={!formData.machineId}>Suivant</Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
             <label className="block text-sm font-medium text-slate-700">Période & Client</label>
             <div className="grid grid-cols-2 gap-4">
                <Input label="Date Début" type="date" required min={new Date().toISOString().split('T')[0]} value={formData.startDate || ''} onChange={e => { setFormData({...formData, startDate: e.target.value}); setError(''); }} hint="Jour de départ de la machine" />
                <Input label="Date Fin" type="date" required min={formData.startDate || new Date().toISOString().split('T')[0]} value={formData.endDate || ''} onChange={e => { setFormData({...formData, endDate: e.target.value}); setError(''); }} hint="Jour de retour prévu" />
             </div>
             {dateConflict && (
               <div className="text-red-600 text-sm bg-red-50 border border-red-200 p-3 rounded flex items-start gap-2">
                 <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                 <div>
                   <span className="font-medium">Conflit de dates !</span> Cette machine est déjà {dateConflict.status === DocStatus.ACTIVE ? 'en cours de location' : 'réservée'} du {dateConflict.startDate} au {dateConflict.endDate}
                   {dateConflict.clientName && <> pour <b>{dateConflict.clientName}</b></>}.
                 </div>
               </div>
             )}
             {error && !dateConflict && <div className="text-red-500 text-sm bg-red-50 p-2 rounded">{error}</div>}
             
             <div>
               <label className="block text-sm font-medium text-slate-700 mb-1">Client <span className="text-red-500 ml-0.5">*</span></label>
               <select
                  className="w-full border-slate-300 rounded-md py-2"
                  value={formData.clientId}
                  onChange={e => {
                    const c = clients.find(cl => cl.id === e.target.value);
                    setFormData({...formData, clientId: e.target.value, clientName: c?.company || c?.name});
                  }}
               >
                 <option value="">Sélectionner un client...</option>
                 {clients.map(c => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}
               </select>
               <FieldHint>Locataire responsable du contrat</FieldHint>
             </div>

             {days > 0 && (
               <div className="bg-slate-50 p-3 rounded text-sm flex justify-between items-center border border-slate-200">
                  <span>Durée : <b>{days} jours</b></span>
                  <span>Total : <b className="text-lg text-primary-700">{formatCurrency(total)}</b></span>
               </div>
             )}

             <div className="flex gap-3">
               <Button variant="ghost" onClick={() => setStep(1)} className="flex-1">Retour</Button>
               <Button className="flex-1" onClick={() => {
                 if (formData.startDate && formData.endDate && new Date(formData.endDate) <= new Date(formData.startDate)) {
                   setError('La date de fin doit être postérieure à la date de début');
                   return;
                 }
                 if(validateAvailability()) {
                   setFormData(prev => ({...prev, depositAmount: Math.round(total * 0.3)}));
                   setStep(3);
                 }
               }} disabled={!formData.startDate || !formData.endDate || !formData.clientId || !!dateConflict}>Suivant</Button>
             </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
             <label className="block text-sm font-medium text-slate-700">Caution / Dépôt de garantie (30% du total)</label>
             <Input
               label="Montant Caution" type="number" required
               value={formData.depositAmount || ''}
               readOnly
               className="bg-slate-100 cursor-not-allowed"
               hint="Calculée automatiquement à 30% du montant total de la location"
             />
             <div className="bg-blue-50 text-blue-700 text-sm p-3 rounded">
                La caution est calculée automatiquement à 30% de la facture de location et sera restituée si aucun dommage.
             </div>
             
             <div className="flex gap-3">
               <Button variant="ghost" onClick={() => setStep(2)} className="flex-1">Retour</Button>
               <Button className="flex-1" onClick={() => setStep(4)} disabled={!formData.depositAmount}>Suivant</Button>
             </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4 text-center">
             <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-2">
               <CheckCircle className="w-6 h-6 text-green-600" />
             </div>
             <h3 className="font-bold text-slate-900">Validation du Contrat</h3>
             <p className="text-sm text-slate-500">Récapitulatif de la location pour <b>{formData.clientName}</b>.</p>
             
             <div className="bg-slate-50 p-4 rounded text-left text-sm space-y-2 border border-slate-200">
                <div className="flex justify-between"><span>Machine:</span> <b>{formData.machineName}</b></div>
                <div className="flex justify-between"><span>Dates:</span> <span>{formData.startDate} au {formData.endDate}</span></div>
                <div className="flex justify-between"><span>Total Loc.:</span> <b>{formatCurrency(total)}</b></div>
                <div className="flex justify-between text-orange-600"><span>Caution:</span> <b>{formatCurrency(formData.depositAmount || 0)}</b></div>
             </div>

             <div className="flex gap-3 pt-2">
               <Button variant="ghost" onClick={() => setStep(3)} className="flex-1">Retour</Button>
               <Button className="flex-1" loading={isSaving} onClick={() => onSave({
                 ...formData,
                 totalAmount: total,
               })}>Confirmer & Créer</Button>
             </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

// --- Rental Module Main ---

export const RentalModule = ({ canWrite, userRole, userName }: { canWrite?: boolean; userRole?: string; userName?: string }) => {
  const isMagasinier = userRole === 'MAGASINIER';
  const isCommercial = userRole === 'COMMERCIAL';
  const isManager = userRole === 'ADMIN' || userRole === 'GERANT';
  const isComptable = userRole === 'COMPTABLE' || userRole === 'ADMIN';
  const canWriteRentals = (canWrite ?? false) && !isMagasinier;
  const canManageRentals = (canWrite ?? false) && !isMagasinier && !isCommercial;
  const canDoConditionReport = canWrite ?? false;
  const { toast } = useToast();
  const paymentMethods = usePaymentMethods();
  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [conditionReportType, setConditionReportType] = useState<'ENTRY' | 'EXIT' | null>(null);

  // Edit draft modal
  const [editingContract, setEditingContract] = useState<(Contract & { uuid: string }) | null>(null);
  const [editForm, setEditForm] = useState({ date_fin_prevue: '', tarif_journalier: '', notes: '', conditions_particulieres: '' });
  const [editLoading, setEditLoading] = useState(false);

  // Deposit payment modal
  const [depositContract, setDepositContract] = useState<(Contract & { uuid: string }) | null>(null);
  const [depositForm, setDepositForm] = useState<PaymentDetails>({ ...EMPTY_PAYMENT_DETAILS });
  const [depositLoading, setDepositLoading] = useState(false);

  // API data
  const [machines, setMachines] = useState<Machine[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [contracts, setContracts] = useState<(Contract & { uuid: string })[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Server-side pagination, sort, search for list view
  const PAGE_SIZE = 20;
  const [listPage, setListPage] = useState(1);
  const [listPagination, setListPagination] = useState({ totalPages: 1, totalItems: 0 });
  const [listContracts, setListContracts] = useState<(Contract & { uuid: string })[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listFilter, setListFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [listStatusFilter, setListStatusFilter] = useState<string>('');

  type RentalSortKey = 'code' | 'clientName' | 'machineName' | 'dateDebut' | 'dateFinPrevue' | 'montant' | 'status';
  const [listSort, setListSort] = useState<{ key: RentalSortKey; dir: 'asc' | 'desc' }>({ key: 'dateDebut', dir: 'desc' });

  const sortKeyMap: Record<RentalSortKey, string> = {
    code: 'code', clientName: 'client_nom', machineName: 'machine_nom',
    dateDebut: 'date_debut', dateFinPrevue: 'date_fin_prevue',
    montant: 'montant_total_prevu', status: 'status',
  };

  const toggleListSort = (key: RentalSortKey) => {
    setListSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
    setListPage(1);
  };

  const RentalSortIcon = ({ col }: { col: RentalSortKey }) => {
    if (listSort.key !== col) return <ArrowUpDown className="w-3 h-3 ml-1 inline opacity-30" />;
    return listSort.dir === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 inline text-primary-600" />
      : <ArrowDown className="w-3 h-3 ml-1 inline text-primary-600" />;
  };

  // Debounce search (400ms)
  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedFilter(listFilter); setListPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [listFilter]);

  // Fetch list view data (server-side)
  const fetchListContracts = useCallback(async () => {
    setListLoading(true);
    try {
      const params: any = {
        page: listPage,
        page_size: PAGE_SIZE,
        sort_by: sortKeyMap[listSort.key],
        sort_dir: listSort.dir,
      };
      if (debouncedFilter.trim()) params.search = debouncedFilter.trim();
      if (listStatusFilter) params.status = listStatusFilter;
      const res = await rentalService.listContracts(params);
      setListContracts((res.data || []).map(transformContract));
      setListPagination({
        totalPages: res.pagination.total_pages,
        totalItems: res.pagination.total_items,
      });
    } catch { /* handled */ }
    finally { setListLoading(false); }
  }, [listPage, debouncedFilter, listSort, listStatusFilter]);

  useEffect(() => {
    if (view === 'list') fetchListContracts();
  }, [view, fetchListContracts]);

  // Fetch calendar/reference data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [machinesRes, clientsRes, contractsRes, loaRes] = await Promise.all([
        stockService.listStock({ type: 'MACHINE', page_size: 100 }).catch(() => ({ data: [] })),
        clientsService.list({ page_size: 100 }).catch(() => ({ data: [] })),
        rentalService.listContracts({ page_size: 100 }).catch(() => ({ data: [] })),
        loaService.listContracts({ page_size: 100 }).catch(() => ({ data: [] })),
      ]);
      setMachines((machinesRes.data || []).map((s: any) => transformMachine({
        id: s.id,
        designation: s.designation,
        numero_serie: s.numero_serie,
        categorie_nom: s.categorie_nom,
        status: s.status,
        tarif_location_journalier: s.tarif_location_journalier || 0,
        prix_vente_public: s.prix_vente_public || 0,
        stock_total: s.stock_total || 0,
      })));
      setClients((clientsRes.data || []).map(transformClient));
      const rentalContracts = (contractsRes.data || []).map(transformContract);
      const loaContracts = (loaRes.data || []).map((c: any) => ({
        id: c.code || c.id,
        uuid: c.id,
        type: ContractType.LOA,
        clientId: c.client_id,
        clientName: c.client_nom,
        machineId: c.machine_id,
        machineName: c.machine_nom,
        startDate: c.date_debut,
        endDate: c.date_fin,
        status: mapContractStatus(c.status),
        dailyRate: 0,
        totalAmount: c.montant_total_finance || 0,
        depositAmount: c.apport_initial || 0,
        depositStatus: 'PAID' as const,
        notes: '',
      })) as (Contract & { uuid: string })[];
      setContracts([...rentalContracts, ...loaContracts]);
    } catch (error) {
      console.error('Error fetching rental data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitForApproval = async (contract: Contract & { uuid: string }) => {
    try {
      await rentalService.submitForApproval(contract.uuid);
      toast('Contrat soumis pour approbation', 'success');
      fetchData();
      fetchListContracts();
    } catch (err: any) {
      toast('Erreur: ' + (err?.response?.data?.detail || err.message || 'Impossible de soumettre'), 'error');
    }
  };

  const handleValidateContract = async (contract: Contract & { uuid: string }) => {
    try {
      await rentalService.validateContract(contract.uuid);
      toast('Contrat validé avec succès', 'success');
      fetchData();
      fetchListContracts();
    } catch (err: any) {
      toast('Erreur: ' + (err?.response?.data?.detail || err.message || 'Impossible de valider'), 'error');
    }
  };

  const [approveTarget, setApproveTarget] = useState<(Contract & { uuid: string }) | null>(null);
  const [approveSiteId, setApproveSiteId] = useState('');
  const [approveSites, setApproveSites] = useState<{ id: string; nom: string; ville?: string }[]>([]);
  const [approving, setApproving] = useState(false);

  const openApproveModal = (contract: Contract & { uuid: string }) => {
    setApproveTarget(contract);
    setApproveSiteId('');
    stockService.listSites().then(setApproveSites).catch(() => setApproveSites([]));
  };

  const handleApproveContract = async () => {
    if (!approveTarget) return;
    setApproving(true);
    try {
      await rentalService.approveContract(approveTarget.uuid, approveSiteId || undefined);
      toast('Contrat approuvé par le gérant', 'success');
      setApproveTarget(null);
      fetchData();
      fetchListContracts();
    } catch (err: any) {
      toast('Erreur: ' + (err?.response?.data?.detail || err?.response?.data?.message || err.message || 'Impossible d\'approuver'), 'error');
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPdf = async (contract: Contract & { uuid: string }) => {
    try {
      const blob = await rentalService.getContractPdf(contract.uuid);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${contract.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast('Erreur lors du téléchargement du PDF: ' + (err?.message || 'Veuillez réessayer'), 'error');
    }
  };

  const handleMarkDepositPaid = (contract: Contract & { uuid: string }) => {
    setDepositForm({ ...EMPTY_PAYMENT_DETAILS });
    setDepositContract(contract);
  };

  const handleConfirmDepositPaid = async () => {
    if (!depositContract) return;
    if (!isPaymentValid(depositForm)) {
      toast('Veuillez remplir tous les champs de paiement obligatoires', 'error');
      return;
    }
    setDepositLoading(true);
    try {
      await rentalService.markDepositPaid(depositContract.uuid, {
        mode_paiement: depositForm.mode_paiement,
        reference: depositForm.reference || undefined,
        banque: depositForm.banque || undefined,
        numero_telephone: depositForm.numero_telephone || undefined,
        transaction_id: depositForm.transaction_id || undefined,
      } as any);
      toast('Caution enregistrée comme payée', 'success');
      setDepositContract(null);
      fetchData();
      fetchListContracts();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err.message || 'Impossible d\'enregistrer le paiement';
      toast('Erreur: ' + (typeof detail === 'string' ? detail : JSON.stringify(detail)), 'error');
    } finally {
      setDepositLoading(false);
    }
  };

  const handleDownloadDepositReceipt = async (contract: Contract & { uuid: string }) => {
    try {
      const blob = await rentalService.getDepositReceiptPdf(contract.uuid);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `recu_caution_${contract.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast('Erreur lors du téléchargement du reçu de caution: ' + (err?.message || 'Veuillez réessayer'), 'error');
    }
  };

  const handleDownloadRefundReceipt = async (contract: Contract & { uuid: string }) => {
    try {
      const blob = await rentalService.getRefundReceiptPdf(contract.uuid);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `recu_remboursement_${contract.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast('Erreur lors du téléchargement du reçu de remboursement: ' + (err?.message || 'Veuillez réessayer'), 'error');
    }
  };

  // Refund modal state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundContract, setRefundContract] = useState<any>(null);
  const [refundMontant, setRefundMontant] = useState(0);
  const [refundModePaiement, setRefundModePaiement] = useState('ESPECES');
  const [refundReference, setRefundReference] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const [refundSignature, setRefundSignature] = useState('');
  const [isRefunding, setIsRefunding] = useState(false);

  const handleRefundDeposit = (contract: Contract & { uuid: string; depositAmount?: number }) => {
    setRefundContract(contract);
    setRefundMontant(contract.depositAmount || 0);
    setRefundModePaiement('ESPECES');
    setRefundReference('');
    setRefundNotes('');
    setRefundSignature('');
    setShowRefundModal(true);
  };

  const handleConfirmRefund = async () => {
    if (!refundSignature) {
      toast('La signature du client est obligatoire', 'error');
      return;
    }
    setIsRefunding(true);
    try {
      await rentalService.refundDeposit(refundContract.uuid, {
        montant: refundMontant,
        mode_paiement: refundModePaiement,
        reference: refundReference || undefined,
        notes: refundNotes || undefined,
        signature_client: refundSignature,
      });
      toast('Caution remboursée avec succès', 'success');
      setShowRefundModal(false);
      fetchData();
      fetchListContracts();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err.message || 'Impossible de rembourser la caution';
      toast('Erreur: ' + (typeof detail === 'string' ? detail : JSON.stringify(detail)), 'error');
    } finally {
      setIsRefunding(false);
    }
  };

  const handleEditDraft = async (contract: Contract & { uuid: string }) => {
    try {
      const full = await rentalService.getContract(contract.uuid);
      setEditForm({
        date_fin_prevue: full.date_fin_prevue || '',
        tarif_journalier: String(full.tarif_journalier || ''),
        notes: full.notes || '',
        conditions_particulieres: (full as any).conditions_particulieres || '',
      });
      setEditingContract(contract);
    } catch (err: any) {
      toast('Erreur lors du chargement du contrat', 'error');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingContract) return;
    setEditLoading(true);
    try {
      const data: RentalContractUpdate = {};
      if (editForm.date_fin_prevue) data.date_fin_prevue = editForm.date_fin_prevue;
      if (editForm.tarif_journalier) data.tarif_journalier = Number(editForm.tarif_journalier);
      data.notes = editForm.notes || undefined;
      data.conditions_particulieres = editForm.conditions_particulieres || undefined;
      await rentalService.updateContract(editingContract.uuid, data);
      toast('Contrat modifié avec succès', 'success');
      setEditingContract(null);
      fetchData();
      fetchListContracts();
    } catch (err: any) {
      toast('Erreur: ' + (err?.response?.data?.detail || err.message || 'Impossible de modifier'), 'error');
    } finally {
      setEditLoading(false);
    }
  };

  const handleOpenConditionReport = (contract: Contract, type: 'ENTRY' | 'EXIT') => {
    setSelectedContract(contract);
    setConditionReportType(type);
  };

  const closeConditionReport = () => {
    setConditionReportType(null);
    setSelectedContract(null);
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleWizardSave = async (contractData: Partial<Contract>) => {
    setIsSaving(true);
    try {
      // Transform to API format
      const createData = {
        client_id: contractData.clientId,
        machine_id: contractData.machineId,
        date_debut: contractData.startDate,
        date_fin_prevue: contractData.endDate,
        montant_caution: contractData.depositAmount,
        tarif_journalier: contractData.totalAmount && contractData.startDate && contractData.endDate
          ? contractData.totalAmount / (Math.floor((new Date(contractData.endDate).getTime() - new Date(contractData.startDate).getTime()) / (1000 * 3600 * 24)))
          : 0,
      };

      await rentalService.createContract(createData);
      toast('Contrat de location créé avec succès', 'success');
      setIsWizardOpen(false);
      await fetchData(); // Refresh data after saving
    } catch (error: any) {
      console.error('Error creating rental contract:', error);
      const detail = error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Veuillez réessayer';
      toast('Erreur lors de la création du contrat: ' + (typeof detail === 'string' ? detail : JSON.stringify(detail)), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
           <h2 className="text-2xl font-bold text-slate-800">Gestion Locations</h2>
           <div className="flex gap-4 mt-2 border-b border-transparent">
              <button onClick={() => setView('calendar')} className={`pb-2 text-sm font-medium border-b-2 transition-colors ${view === 'calendar' ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>Planning</button>
              <button onClick={() => setView('list')} className={`pb-2 text-sm font-medium border-b-2 transition-colors ${view === 'list' ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>Contrats Actifs</button>
           </div>
        </div>
        {canWriteRentals && <Button icon={Plus} onClick={() => setIsWizardOpen(true)} className="whitespace-nowrap flex-shrink-0">Nouveau Contrat</Button>}
      </div>

      {/* Content */}
      <div className="min-h-[500px]">
        {view === 'calendar' ? (
          <RentalCalendar machines={machines} contracts={contracts} isLoading={isLoading} />
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
             {/* Search bar */}
             <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
               <h3 className="font-bold text-slate-700">Contrats de location</h3>
               <div className="relative w-64">
                 <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                 <input
                   className="pl-8 w-full border-slate-300 rounded-md text-sm py-1"
                   placeholder="Rechercher code, client, machine..."
                   value={listFilter}
                   onChange={e => setListFilter(e.target.value)}
                 />
               </div>
             </div>
             {listLoading ? (
               <div className="flex justify-center py-12">
                 <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
               </div>
             ) : (
             <>
             <table className="min-w-full divide-y divide-slate-200">
               <thead className="bg-slate-50">
                 <tr>
                   <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleListSort('code')}>Contrat<RentalSortIcon col="code" /></th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleListSort('machineName')}>Machine<RentalSortIcon col="machineName" /></th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleListSort('clientName')}>Client<RentalSortIcon col="clientName" /></th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleListSort('dateDebut')}>Début<RentalSortIcon col="dateDebut" /></th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleListSort('dateFinPrevue')}>Fin prévue<RentalSortIcon col="dateFinPrevue" /></th>
                   {!isMagasinier && <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleListSort('montant')}>Montant<RentalSortIcon col="montant" /></th>}
                   <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleListSort('status')}>Statut<RentalSortIcon col="status" /></th>
                   <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                 </tr>
               </thead>
               <tbody className="divide-y divide-slate-200">
                 {listContracts.length === 0 ? (
                   <tr><td colSpan={isMagasinier ? 7 : 8} className="text-center py-8 text-slate-500">Aucun contrat de location</td></tr>
                 ) : listContracts.map(c => (
                   <tr key={c.id} className="hover:bg-slate-50">
                     <td className="px-6 py-4 font-medium text-sm text-slate-900">{c.id}</td>
                     <td className="px-6 py-4 text-sm text-slate-600">{c.machineName}</td>
                     <td className="px-6 py-4 text-sm text-slate-600">{c.clientName}</td>
                     <td className="px-6 py-4 text-sm text-slate-600">{c.startDate}</td>
                     <td className="px-6 py-4 text-sm text-slate-600">{c.endDate}</td>
                     {!isMagasinier && <td className="px-6 py-4 text-sm text-right font-medium">{(c.totalAmount || 0).toLocaleString('fr-FR')} F</td>}
                     <td className="px-6 py-4 text-center"><ContractStatusBadge status={c.status} /></td>
                     <td className="px-6 py-4 text-right">
                       <div className="flex justify-end gap-2">
                         {c.status !== DocStatus.DRAFT && (
                           <Button size="sm" variant="ghost" onClick={() => handleDownloadPdf(c)} icon={Download} title="Télécharger PDF">PDF</Button>
                         )}
                         {c.status === DocStatus.DRAFT && canWriteRentals && (
                           <Button size="sm" variant="ghost" onClick={() => handleEditDraft(c)} icon={Pencil}>Modifier</Button>
                         )}
                         {!isCommercial && c.status === DocStatus.DRAFT && (
                           <Button size="sm" onClick={() => handleSubmitForApproval(c)} icon={CheckCircle}>Soumettre</Button>
                         )}
                         {isManager && c.status === DocStatus.PENDING_APPROVAL && (
                           <Button size="sm" variant="primary" onClick={() => openApproveModal(c)} icon={CheckCircle}>Approuver</Button>
                         )}
                         {c.status === DocStatus.VALIDATED && isComptable && (
                           <Button size="sm" variant="primary" onClick={() => handleMarkDepositPaid(c as any)} icon={DollarSign}>Enregistrer caution</Button>
                         )}
                         {c.status === DocStatus.VALIDATED && !isComptable && (
                           <span className="text-xs text-amber-600 font-medium px-2 py-1 bg-amber-50 rounded">En attente caution</span>
                         )}
                         {c.status === DocStatus.CAUTION_PAYEE && (
                           <Button size="sm" variant="ghost" onClick={() => handleDownloadDepositReceipt(c as any)} icon={FileText} title="Reçu de caution">Reçu caution</Button>
                         )}
                         {(isManager || isMagasinier) && c.status === DocStatus.CAUTION_PAYEE && (
                           <Button size="sm" onClick={() => handleOpenConditionReport(c, 'ENTRY')} icon={CheckCircle}>Départ</Button>
                         )}
                         {c.status === DocStatus.ACTIVE && (
                           <Button size="sm" variant="ghost" onClick={() => handleDownloadDepositReceipt(c as any)} icon={FileText} title="Reçu de caution">Reçu caution</Button>
                         )}
                         {(isManager || isMagasinier) && c.status === DocStatus.ACTIVE && (
                           <Button size="sm" variant="secondary" onClick={() => handleOpenConditionReport(c, 'EXIT')} icon={ArrowRight}>Retour</Button>
                         )}
                         {!isCommercial && c.status === DocStatus.CLOSED && isComptable && c.depositStatus === 'PAID' && !(c as any).cautionRestituee && (
                           <Button size="sm" variant="primary" onClick={() => handleRefundDeposit(c as any)} icon={DollarSign}>Rembourser caution</Button>
                         )}
                         {!isCommercial && c.status === DocStatus.CLOSED && (c as any).cautionRestituee && (
                           <Button size="sm" variant="ghost" onClick={() => handleDownloadRefundReceipt(c as any)} icon={FileText} title="Reçu de remboursement">Reçu remboursement</Button>
                         )}
                         {!isCommercial && c.status === DocStatus.CANCELLED && <span className="text-xs text-slate-400 italic">Annulé</span>}
                       </div>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
             {/* Pagination */}
             {listPagination.totalPages > 1 && (
               <div className="px-4 py-3 flex items-center justify-between border-t border-slate-200">
                 <div className="text-sm text-slate-500">
                   {listPagination.totalItems} contrat{listPagination.totalItems > 1 ? 's' : ''}
                 </div>
                 <div className="flex items-center gap-2">
                   <Button
                     variant="ghost"
                     size="sm"
                     disabled={listPage <= 1}
                     onClick={() => setListPage(p => p - 1)}
                   >
                     Précédent
                   </Button>
                   <span className="text-sm text-slate-600">
                     Page {listPage} sur {listPagination.totalPages}
                   </span>
                   <Button
                     variant="ghost"
                     size="sm"
                     disabled={listPage >= listPagination.totalPages}
                     onClick={() => setListPage(p => p + 1)}
                   >
                     Suivant
                   </Button>
                 </div>
               </div>
             )}
             </>
             )}
          </div>
        )}
      </div>

      {/* Modals */}
      {isWizardOpen && <CreateRentalWizard onClose={() => setIsWizardOpen(false)} onSave={handleWizardSave} machines={machines} clients={clients} contracts={contracts} isSaving={isSaving} />}
      
      {conditionReportType && selectedContract && (
        <ConditionReportView
          type={conditionReportType}
          contract={selectedContract}
          machineCategory={machines.find(m => m.id === selectedContract.machineId)?.category}
          userRole={userRole}
          userName={userName}
          onClose={closeConditionReport}
          onSave={() => {
            closeConditionReport();
            fetchData();
            fetchListContracts();
          }}
        />
      )}

      {/* Refund Modal */}
      {showRefundModal && refundContract && (
        <Modal isOpen={showRefundModal} onClose={() => setShowRefundModal(false)} title="Remboursement de Caution">
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg text-sm space-y-1">
              <div><span className="text-slate-500">Contrat:</span> <span className="font-medium">{refundContract.id}</span></div>
              <div><span className="text-slate-500">Client:</span> <span className="font-medium">{refundContract.clientName}</span></div>
              <div><span className="text-slate-500">Machine:</span> <span className="font-medium">{refundContract.machineName}</span></div>
              <div><span className="text-slate-500">Caution:</span> <span className="font-bold">{formatCurrency(refundContract.depositAmount || 0)}</span></div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Montant à rembourser</label>
              <input type="number" className="w-full border-slate-300 rounded-md text-sm py-2" value={refundMontant}
                onChange={e => setRefundMontant(Math.max(0, Math.min(refundContract.depositAmount || 0, parseFloat(e.target.value) || 0)))} />
              {refundMontant < (refundContract.depositAmount || 0) && (
                <p className="text-xs text-amber-600 mt-1">Retenue: {formatCurrency((refundContract.depositAmount || 0) - refundMontant)}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mode de paiement</label>
              <select className="w-full border-slate-300 rounded-md text-sm py-2" value={refundModePaiement} onChange={e => setRefundModePaiement(e.target.value)}>
                {paymentMethods.map(m => <option key={m.code} value={m.code}>{m.label}</option>)}
              </select>
            </div>

            {(refundModePaiement === 'CHEQUE' || refundModePaiement === 'VIREMENT' || refundModePaiement.startsWith('MOBILE')) && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Référence</label>
                <input type="text" className="w-full border-slate-300 rounded-md text-sm py-2" value={refundReference}
                  onChange={e => setRefundReference(e.target.value)} placeholder="N° chèque, transaction..." />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes (optionnel)</label>
              <textarea className="w-full border-slate-300 rounded-md text-sm py-2" rows={2} value={refundNotes}
                onChange={e => setRefundNotes(e.target.value)} placeholder="Motif de retenue, observations..." />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Signature du Client <span className="text-red-500">*</span></label>
              <p className="text-xs text-slate-500 mb-2">Le client doit signer pour confirmer la réception du remboursement.</p>
              <SignaturePad label="Signature Client" onSave={(sig: string) => setRefundSignature(sig)} />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="ghost" onClick={() => setShowRefundModal(false)} disabled={isRefunding}>Annuler</Button>
              <Button variant="primary" onClick={handleConfirmRefund} disabled={isRefunding || !refundSignature}>
                {isRefunding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirmer le remboursement
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Deposit Payment Modal */}
      {depositContract && (
        <Modal isOpen={true} title={`Enregistrer caution — ${depositContract.id}`} onClose={() => setDepositContract(null)}>
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Montant caution:</span> <span className="font-bold">{(depositContract.depositAmount || 0).toLocaleString('fr-FR')} F CFA</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Client:</span> <span>{depositContract.clientName}</span></div>
            </div>
            <PaymentMethodSelect value={depositForm} onChange={setDepositForm} />
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="ghost" onClick={() => setDepositContract(null)} disabled={depositLoading}>Annuler</Button>
              <Button variant="primary" onClick={handleConfirmDepositPaid} disabled={depositLoading || !isPaymentValid(depositForm)}>
                {depositLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirmer le paiement
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Draft Contract Modal */}
      {editingContract && (
        <Modal isOpen={true} title={`Modifier le brouillon ${editingContract.id}`} onClose={() => setEditingContract(null)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date de fin prévue</label>
              <Input type="date" value={editForm.date_fin_prevue}
                onChange={e => setEditForm(f => ({ ...f, date_fin_prevue: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tarif journalier (FCFA)</label>
              <Input type="number" min={1} value={editForm.tarif_journalier}
                onChange={e => setEditForm(f => ({ ...f, tarif_journalier: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
              <textarea className="w-full border border-slate-300 rounded-md text-sm p-2" rows={2}
                value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Notes internes..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Conditions particulières</label>
              <textarea className="w-full border border-slate-300 rounded-md text-sm p-2" rows={3}
                value={editForm.conditions_particulieres} onChange={e => setEditForm(f => ({ ...f, conditions_particulieres: e.target.value }))}
                placeholder="Conditions spécifiques au contrat..." />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="ghost" onClick={() => setEditingContract(null)} disabled={editLoading}>Annuler</Button>
              <Button variant="primary" onClick={handleSaveEdit} disabled={editLoading}>
                {editLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Enregistrer
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {approveTarget && (
        <Modal isOpen={!!approveTarget} onClose={() => setApproveTarget(null)} title={`Approuver le contrat ${approveTarget.id}`}>
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              L'approbation génère automatiquement un Bon de Livraison.
              Choisissez le dépôt d'enlèvement de la machine.
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Dépôt d'enlèvement</label>
              <select
                className="w-full border-slate-300 rounded-md py-2 text-sm"
                value={approveSiteId}
                onChange={e => setApproveSiteId(e.target.value)}
              >
                <option value="">Site par défaut</option>
                {approveSites.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.nom}{s.ville ? ` (${s.ville})` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Prévente : l'approbation est toujours enregistrée. Le BL n'est créé que si le stock est suffisant au dépôt choisi; sinon il devra être créé manuellement après réception.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="ghost" onClick={() => setApproveTarget(null)} disabled={approving}>Annuler</Button>
              <Button variant="primary" onClick={handleApproveContract} disabled={approving} icon={CheckCircle}>
                {approving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Approuver
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
